import {AxiosError} from "axios";
import {useClientV2} from "../../utils/hooks";
import {useQuery} from "@tanstack/react-query";

type TDashboardMetricsApiResponse = {
  totalCustomers: number;
  totalRevenue: {currency?: string; amount?: number}[];
  totalPurchases: number;
  currency: string;
};

type TV2DashboardMetricsApiResponse = {
  revenueByCurrency: Record<string, number>;
  totalDistinctCustomers: number;
  totalPurchases: number;
};

/**
 * Adapts the payments/analytics/metrics (v2) response shape into the
 * flat shape the dashboard UI consumes (array of {currency, amount} pairs
 * plus a default currency). This is a permanent response adapter — not a
 * migration shim — since the v2 API always returns revenue keyed by
 * currency code and the UI needs it as a list it can iterate/select from.
 */
const normalizeV2DashboardMetrics = (
  v2Response: TV2DashboardMetricsApiResponse
): TDashboardMetricsApiResponse => {
  const totalRevenue = Object.entries(v2Response.revenueByCurrency).map(
    ([currency, amount]) => ({
      currency,
      amount,
    })
  );

  const defaultCurrency = totalRevenue[0]?.currency ?? "";

  return {
    totalCustomers: v2Response.totalDistinctCustomers,
    totalRevenue,
    totalPurchases: v2Response.totalPurchases,
    currency: defaultCurrency,
  };
};

const useGetDashboardMetrics = () => {
  const clientV2 = useClientV2();

  return useQuery<
    TDashboardMetricsApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["dashboard_metrics"],
    queryFn: async () => {
      const response = await clientV2("payments/analytics/metrics");
      const data = response?.data ?? response;
      return normalizeV2DashboardMetrics(data);
    },
  });
};

export {useGetDashboardMetrics};
